import api from './api';

// === Tipos ===

export interface FixedExpenseCategory {
  id: number;
  name: string;
  icon?: string;
  color?: string;
  is_active: boolean;
}

export interface FixedExpenseCategoryCreate {
  name: string;
  icon?: string;
  color?: string;
}

export interface FixedExpenseCategoryUpdate {
  name?: string;
  icon?: string;
  color?: string;
  is_active?: boolean;
}

export interface FixedExpense {
  id: number;
  category_id?: number;
  category?: FixedExpenseCategory;
  description?: string;
  amount: number;
  month: number;
  year: number;
  is_recurring: boolean;
  is_paid: boolean;
  paid_at?: string;
  notes?: string;
  created_at: string;
}

export interface FixedExpenseCreate {
  category_id?: number;
  description?: string;  // Opcional - puede usar solo categoría
  amount: number;
  month: number;
  year: number;
  is_recurring?: boolean;
  is_paid?: boolean;
  notes?: string;
}

export interface FixedExpenseUpdate {
  category_id?: number;
  description?: string;
  amount?: number;
  month?: number;
  year?: number;
  is_recurring?: boolean;
  is_paid?: boolean;
  paid_at?: string;
  notes?: string;
}

export interface MonthlyExpenseSummary {
  month: number;
  year: number;
  total_expenses: number;
  total_paid: number;
  total_pending: number;
  expense_count: number;
}

export interface CategoryExpense {
  category: string;
  total: number;
  paid: number;
  pending: number;
  count: number;
}

export interface MonthlyFinancialSummary {
  month: number;
  year: number;
  total_income: number;
  total_profit: number;
  total_cost: number;
  total_variable_expenses: number;
  total_fixed_expenses: number;
  fixed_expenses_paid: number;
  fixed_expenses_pending: number;
  net_result: number;
  expenses_by_category: CategoryExpense[];
}

export interface MonthlyCashFlow {
  month: number;
  year: number;
  total_income: number;
  supplier_payments: number;
  fixed_expenses_paid: number;
  operational_expenses: number;
  salary_expenses: number;
  merchandise_purchases?: number;  // Compra de mercadería: sale plata, no es gasto
  withdrawals: number;
  net_cash_flow: number;
  operating_cash_flow: number;
}

export interface WithdrawalCategoryBreakdown {
  category: string;
  label: string;
  total: number;
  count: number;
  /** Solo para 'salary': destinatario por el que se agrupó. */
  recipient?: string | null;
}

export interface WithdrawalDetailItem {
  id: number;
  date: string | null;
  amount: number;
  category: string;
  label: string;
  description?: string | null;
  recipient?: string | null;
  payment_method?: string | null;
}

export interface WithdrawalDetail {
  year: number;
  month?: number | null;
  category: string;
  label: string;
  total: number;
  count: number;
  items: WithdrawalDetailItem[];
}

export interface CopyRecurringExpensesRequest {
  source_month: number;
  source_year: number;
  target_month: number;
  target_year: number;
}

export interface MonthSummary {
  month: number;
  total_income: number;
  total_profit: number;
  total_cost: number;
  total_variable_expenses: number;
  total_expenses: number;
  net_result: number;
}

export interface YearlyFinancialSummary {
  year: number;
  total_income: number;
  total_profit: number;
  total_cost: number;
  total_variable_expenses: number;
  total_expenses: number;
  net_result: number;
  months: MonthSummary[];
}

export interface ExpenseFilters {
  month?: number;
  year?: number;
  category_id?: number;
  is_paid?: boolean;
}

// === Desgloses Detallados ===

export interface ProductCostDetail {
  product_id: number;
  product_name: string;
  quantity_sold: number;
  unit_cost: number;
  total_cost: number;
}

export interface ProductCostBreakdown {
  year: number;
  month?: number;
  total_cost: number;
  products: ProductCostDetail[];
}

export interface ServiceSaleDetail {
  product_id?: number;
  name: string;
  quantity: number;   // Cuántas veces se cobró
  total: number;      // Cuánto sumó
}

export interface ServiceSalesBreakdown {
  date_from: string;
  date_to: string;
  total: number;
  services_count: number;  // Servicios distintos
  items_count: number;     // Servicios cobrados en total
  services: ServiceSaleDetail[];
}

export interface SaleDetail {
  sale_id: number;
  date: string;
  total: number;
  payment_method: string;
  items_count: number;
  owner_name?: string;
}

export interface SalesBreakdown {
  year: number;
  month?: number;
  total_sales: number;
  sales_count: number;
  by_payment_method: Record<string, number>;
  sales: SaleDetail[];
}

// === Servicio ===

export const financeService = {
  // === Categorías ===

  getCategories: async (includeInactive = false): Promise<FixedExpenseCategory[]> => {
    const response = await api.get('/finance/categories', {
      params: { include_inactive: includeInactive }
    });
    return response.data;
  },

  getCategory: async (id: number): Promise<FixedExpenseCategory> => {
    const response = await api.get(`/finance/categories/${id}`);
    return response.data;
  },

  createCategory: async (data: FixedExpenseCategoryCreate): Promise<FixedExpenseCategory> => {
    const response = await api.post('/finance/categories', data);
    return response.data;
  },

  updateCategory: async (id: number, data: FixedExpenseCategoryUpdate): Promise<FixedExpenseCategory> => {
    const response = await api.put(`/finance/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: number): Promise<void> => {
    await api.delete(`/finance/categories/${id}`);
  },

  // === Gastos ===

  getExpenses: async (filters?: ExpenseFilters): Promise<FixedExpense[]> => {
    const response = await api.get('/finance/expenses', { params: filters });
    return response.data;
  },

  getExpense: async (id: number): Promise<FixedExpense> => {
    const response = await api.get(`/finance/expenses/${id}`);
    return response.data;
  },

  createExpense: async (data: FixedExpenseCreate): Promise<FixedExpense> => {
    const response = await api.post('/finance/expenses', data);
    return response.data;
  },

  updateExpense: async (id: number, data: FixedExpenseUpdate): Promise<FixedExpense> => {
    const response = await api.put(`/finance/expenses/${id}`, data);
    return response.data;
  },

  deleteExpense: async (id: number): Promise<void> => {
    await api.delete(`/finance/expenses/${id}`);
  },

  togglePaid: async (id: number): Promise<FixedExpense> => {
    const response = await api.post(`/finance/expenses/${id}/toggle-paid`);
    return response.data;
  },

  // === Resúmenes ===

  getMonthlyExpenseSummary: async (year: number, month: number): Promise<MonthlyExpenseSummary> => {
    const response = await api.get(`/finance/summary/expenses/${year}/${month}`);
    return response.data;
  },

  getMonthlyFinancialSummary: async (year: number, month: number): Promise<MonthlyFinancialSummary> => {
    const response = await api.get(`/finance/summary/financial/${year}/${month}`);
    return response.data;
  },

  getMonthlyCashFlow: async (year: number, month: number): Promise<MonthlyCashFlow> => {
    const response = await api.get(`/finance/summary/cash-flow/${year}/${month}`);
    return response.data;
  },

  getYearlyFinancialSummary: async (year: number): Promise<YearlyFinancialSummary> => {
    const response = await api.get(`/finance/summary/yearly/${year}`);
    return response.data;
  },

  copyRecurringExpenses: async (data: CopyRecurringExpensesRequest): Promise<FixedExpense[]> => {
    const response = await api.post('/finance/expenses/copy-recurring', data);
    return response.data;
  },

  // === Desgloses Detallados ===

  getProductCostBreakdown: async (year: number, month?: number): Promise<ProductCostBreakdown> => {
    const response = await api.get(`/finance/summary/products/${year}`, {
      params: month ? { month } : undefined
    });
    return response.data;
  },

  // Total por servicio de un período (mismo día en ambas fechas = un solo día)
  getServiceSalesBreakdown: async (dateFrom: string, dateTo: string): Promise<ServiceSalesBreakdown> => {
    const response = await api.get('/finance/summary/services', {
      params: { date_from: dateFrom, date_to: dateTo },
    });
    return response.data;
  },

  getSalesBreakdown: async (year: number, month?: number): Promise<SalesBreakdown> => {
    const response = await api.get(`/finance/summary/sales/${year}`, {
      params: month ? { month } : undefined
    });
    return response.data;
  },

  getWithdrawalBreakdown: async (year: number, month: number): Promise<WithdrawalCategoryBreakdown[]> => {
    const response = await api.get(`/finance/summary/withdrawal-breakdown/${year}/${month}`);
    return response.data;
  },

  getWithdrawalDetail: async (
    year: number,
    params: { month?: number; category?: string; recipient?: string }
  ): Promise<WithdrawalDetail> => {
    const response = await api.get(`/finance/summary/withdrawal-detail/${year}`, { params });
    return response.data;
  },
};

// === Labels y Constantes ===

export const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export const CATEGORY_COLORS: Record<string, string> = {
  blue: 'bg-blue-100 text-blue-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  purple: 'bg-purple-100 text-purple-800',
  cyan: 'bg-cyan-100 text-cyan-800',
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-800',
  orange: 'bg-orange-100 text-orange-800',
  gray: 'bg-gray-100 text-gray-800',
  slate: 'bg-slate-100 text-slate-800',
};
